import React, { Fragment, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import pluralize from 'pluralize';
import compose from 'lodash.flowright';
import { matchPath, withRouter } from 'react-router-dom';
import { graphql, useQuery, withApollo } from 'react-apollo';
import { mapProps } from 'recompose';
import { connect } from 'react-redux';

import CheckoutCartItem from '../CheckoutCartItem';
import CheckoutCartAddress from './CheckoutCartAddress';
import PriceBreakdownList from '../PriceBreakdownList';
import CheckoutCartPaymentPanel from './CheckoutCartPaymentPanel';
import getAccount from '../../../../apps/temp/graphql/queries/getAccount';
import {
  getCheckoutCart,
  getCheckoutCartCheckoutItems,
  getCheckoutCartTotalItems,
  getCheckoutCartTotalPrice,
  getCheckoutPreviousCart,
  checkoutCartRequiresAddress,
  getUnconfiguredAlbumPrintPackageCartItems,
  getUnconfiguredPurchasedPrintPackageCartItems,
  getUnconfiguredTaggedAlbumPrintPackageCartItems,
  checkoutCartRequiresPrepaidNotice,
} from '../../redux/checkoutSelectors';
import {
  clearCart as clearCartAction,
  onCartItemRemove as onCartItemRemoveAction,
} from '../../redux/checkoutActions';
import { openAlert as openAlertAction } from '../../../../actions/modal-actions';
import { getCheckoutPriceQuery } from '../../../../apps/temp/graphql/queries/getCheckoutPrice';
import { checkout as checkoutMutation } from '../../../../graphql/mutations/checkout';
import CART_ITEM from '../../constants/cartItems';
import { CURRENCY } from '../../../../constants/currency';
import useCheckoutCart from './hooks/useCheckoutCart';
import { getErrorString } from '../../../../util/normalize-errors';
import PhoneAuthWithEmailFallback from '../../../../components/auth/PhoneAuthWithEmailFallback';
import { trackAuthEvent } from '../../../../apps/web-gallery/utilities/analytics';
import Spinner from '../../../../components/global/Spinner';
import CheckoutCartPendingPurchases from './CheckoutCartPendingPurchases';
import {
  containSingleVendorItems,
  containsTransactionsMissingPrepaidItems,
} from './helpers';
import Alert from '../../../../components/global/Alert';
import usePurchasesPendingConfiguration from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasesPendingConfiguration/hooks/usePurchasesPendingConfiguration';
import { getAlbumMembershipGroupQuery } from '../../../../apps/temp/views/memberships/AlbumMembershipGroup/graphql/queries/getAlbumMembershipGroup';
import { AlbumFolderForHierarchyShippingSettings } from '../../../../graphql/queries/album-folder';
import useContributorBusinessCard from '../../../../components/global/ContributorBusinessCard/hooks/useContributorBusinessCard';
import { getAlbumMembershipGroupEntityAccount } from '../../../../apps/temp/views/memberships/AlbumMembershipGroup/helpers';
import { getWatermarkSettingsQuery } from '../../../../apps/temp/views/memberships/AlbumMembershipGroup/graphql/queries/getWatermarkSettings';
import { extractWatermarkSettings } from '../../../../util/watermark-settings';
import CheckoutPrintOrderServices from './CheckoutCartPrintOrderServices/CheckoutPrintOrderServices';

const propTypes = {
  className: PropTypes.string,
  account: PropTypes.shape({
    id: PropTypes.string,
    addresses: PropTypes.array,
  }),
  cart: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.oneOf(Object.values(CART_ITEM)),
      albumFolder: PropTypes.object,
      album: PropTypes.object,
      albumCustomPrint: PropTypes.object,
      shipping: PropTypes.bool,
    }),
  ).isRequired,
  checkoutItems: PropTypes.object,
  totalCartItems: PropTypes.number,
  computedPrice: PropTypes.shape({
    value: PropTypes.number,
    currency: PropTypes.oneOf(Object.values(CURRENCY)),
  }),
  checkout: PropTypes.func.isRequired,
  openAlert: PropTypes.func.isRequired,
  pricing: PropTypes.shape({
    total_price: PropTypes.object,
    total_shipping_cost: PropTypes.object,
    total_tax: PropTypes.object,
  }),
  loadingPricing: PropTypes.bool,
  errorFetchingPricing: PropTypes.object,
  refetchPricing: PropTypes.func,
  requiresAddress: PropTypes.bool,
  requiredPrepaidNotice: PropTypes.bool,
  itemsPriceBreakdown: PropTypes.arrayOf(
    PropTypes.shape({
      checkout_item_client_id: PropTypes.string,
      price: PropTypes.object,
      discount_price: PropTypes.object,
    }),
  ),
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  clearCart: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  checkoutRequirements: PropTypes.arrayOf(
    PropTypes.shape({
      message: PropTypes.string.isRequired,
      condition: PropTypes.bool.isRequired,
      scrollElement: PropTypes.string,
    }).isRequired,
  ),
  onClose: PropTypes.func,
  client: PropTypes.shape({
    resetStore: PropTypes.func,
  }),
  authenticated: PropTypes.bool,
  unconfiguredAlbumPrintPackageCartItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
    }),
  ),
  unconfiguredPurchasedPrintPackageCartItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
    }),
  ),
  unconfiguredTaggedAlbumPrintPackageCartItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
    }),
  ),
  stripeModalProps: PropTypes.shape({
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  }),
  hierarchyItemId: PropTypes.string,
  isValidSingleShippingDate: PropTypes.bool,
  vendor: PropTypes.shape({
    id: PropTypes.string,
  }),
};

const CheckoutCart = ({
  className,
  cart,
  checkoutItems,
  totalCartItems,
  account,
  computedPrice,
  checkout,
  openAlert,
  pricing,
  loadingPricing,
  refetchPricing,
  errorFetchingPricing,
  requiresAddress,
  requiredPrepaidNotice,
  location,
  itemsPriceBreakdown = [],
  clearCart,
  onSuccess,
  checkoutRequirements = [],
  onClose,
  client,
  authenticated,
  unconfiguredAlbumPrintPackageCartItems,
  unconfiguredPurchasedPrintPackageCartItems,
  unconfiguredTaggedAlbumPrintPackageCartItems,
  stripeModalProps,
  hierarchyItemId: defaultHierarchyItemId,
  isValidSingleShippingDate,
  vendor,
}) => {
  const match = matchPath(location.pathname, {
    path: '/app/shutterbug/hierarchy-items/:hierarchyItemId',
    exact: false,
  });
  const consumersMatch = matchPath(location.pathname, {
    path: '/consumer/matches/:albumMembershipGroupId',
    exact: false,
  });

  const albumMembershipGroupId =
    match?.params?.hierarchyItemId ??
    consumersMatch?.params?.albumMembershipGroupId;

  const { data: { albumMembershipGroup } = {} } = useQuery(
    getAlbumMembershipGroupQuery,
    {
      variables: {
        id: albumMembershipGroupId,
      },
      skip: !albumMembershipGroupId,
      fetchPolicy: 'cache-first',
    },
  );

  const { data: { hierarchyItem } = {} } = useQuery(getWatermarkSettingsQuery, {
    variables: {
      id: albumMembershipGroupId,
    },
    skip: !albumMembershipGroupId || albumMembershipGroup,
    fetchPolicy: 'cache-first',
  });

  const vendorAccount = getAlbumMembershipGroupEntityAccount(
    albumMembershipGroup || hierarchyItem,
  );

  const watermarkSettings = useMemo(
    () => extractWatermarkSettings(vendorAccount),
    [vendorAccount],
  );

  let hierarchyItemId = defaultHierarchyItemId;

  if (!hierarchyItemId) {
    if (albumMembershipGroup) {
      if (albumMembershipGroup.is_album) {
        hierarchyItemId = albumMembershipGroup.album.root_album_folder?.id;
      } else {
        hierarchyItemId = albumMembershipGroup.id;
      }
    } else if (hierarchyItem?.album) {
      hierarchyItemId = hierarchyItem?.album?.root_album_folder?.id;
    } else {
      hierarchyItemId = hierarchyItem?.album_folder?.root?.id;
    }
  }

  const classes = classNames({
    [className]: !!className,
    'checkout-cart': true,
  });

  const {
    prepaidPrintPackagesMissingConfiguration,
    prepaidPhotoPricingsMissingConfiguration,
    prepaidCustomPrintsMissingConfiguration,
  } = usePurchasesPendingConfiguration({
    hierarchyItemId,
    shippableOnly: false,
  });

  const {
    name: vendorName,
    photoAttributionPage: vendorAttribution,
  } = useContributorBusinessCard({ accountId: vendor?.id });

  const { onCheckout, allowedPromoCodes, submitting } = useCheckoutCart({
    mutation: checkout,
    onSuccess: () => {
      // We clear the cart on success, as avoiding to do so will mean checkoutPrice will be recalled with
      // these items which could fail if we have Photo Download types, since these are blocked from re-purchase.
      clearCart();
      onSuccess?.({ cart });
    },
    onError: error => {
      openAlert({
        message: getErrorString(error),
        info: error?.request_id || '',
      });
    },
    openAlert,
    pricing,
    cart,
    checkoutItems,
    loadingPricing,
    refetchPricing,
    errorFetchingPricing,
  });

  useEffect(() => {
    if (totalCartItems === 0) {
      onClose?.();
    }
  }, [totalCartItems]);

  if (!authenticated) {
    return (
      <div className="p-4">
        <PhoneAuthWithEmailFallback
          introText={
            'In order to complete a purchase we need your phone number. ' +
            'Waldo will text a verification code to the number below.'
          }
          onAuthSuccess={authType => {
            client.resetStore();
            trackAuthEvent(authType);
          }}
          defaultEmailStep="LOGIN"
        />
      </div>
    );
  }

  if (!account) {
    return (
      <div
        data-testid="loading-spinner"
        className="w-100 d-flex align-items-center justify-content-center p-4"
      >
        <Spinner />
      </div>
    );
  }

  const [address] = account?.addresses ?? [];

  const containSingleVendorItemsBool = containSingleVendorItems(cart);
  const containsTransactionsMissingPrepaidItemsBool = containsTransactionsMissingPrepaidItems(
    cart,
    prepaidPrintPackagesMissingConfiguration,
    prepaidPhotoPricingsMissingConfiguration,
    prepaidCustomPrintsMissingConfiguration,
  );

  const requirements = [
    ...checkoutRequirements,
    {
      message: 'Please add a shipping address.',
      condition: !(requiresAddress && !address && !isValidSingleShippingDate),
      scrollElement: 'checkout-shipping-address',
    },
    {
      message: 'Some items in your cart require you to complete configuration.',
      condition:
        (unconfiguredAlbumPrintPackageCartItems?.length ?? 0) === 0 &&
        (unconfiguredPurchasedPrintPackageCartItems?.length ?? 0) === 0 &&
        (unconfiguredTaggedAlbumPrintPackageCartItems?.length ?? 0) === 0,
    },
    {
      message:
        'You have items that belong to multiple vendors. Ensure you only select items from a single seller in order to continue..',
      condition: containSingleVendorItemsBool,
    },
    {
      message:
        'Items pre-purchased at the same time must be checked out within a single transaction',
      condition: !containsTransactionsMissingPrepaidItemsBool,
    },
    {
      message: 'All pre-purchased items must be configured before proceeding',
      condition:
        prepaidPrintPackagesMissingConfiguration.length === 0 &&
        prepaidPhotoPricingsMissingConfiguration.length === 0 &&
        prepaidCustomPrintsMissingConfiguration.length === 0,
    },
  ];

  const totalComputedPrice =
    computedPrice.value +
    (pricing?.total_shipping_cost.value ?? 0) +
    (pricing?.total_tax.value ?? 0);

  return (
    <Fragment>
      <CheckoutCartPendingPurchases
        hierarchyItemId={hierarchyItemId}
        watermarkSettings={watermarkSettings}
      />
      <div className={classes}>
        <div className="d-flex align-items-center mb-2">
          <h3 className="m-0 text-extra-bold text-faded">Order Summary</h3>

          <span className="text-sm ml-auto text-faded text-bold">
            {totalCartItems} {pluralize('item', totalCartItems)}
          </span>
        </div>

        <div className="mb-4">
          {cart.map(item => {
            const price = itemsPriceBreakdown.find(
              priceBreakdown =>
                priceBreakdown.checkout_item_client_id === item.id,
            );

            return (
              <CheckoutCartItem
                key={item.id}
                item={item}
                loading={loadingPricing}
                pricing={price}
                watermarkSettings={watermarkSettings}
              />
            );
          })}
          {!isValidSingleShippingDate && (
            <CheckoutPrintOrderServices
              albumMembershipGroup={albumMembershipGroup}
              itemsPriceBreakdown={itemsPriceBreakdown}
            />
          )}
        </div>

        {requiresAddress && !isValidSingleShippingDate && (
          <CheckoutCartAddress
            account={account}
            checkoutItems={checkoutItems}
            addressRequired
          />
        )}
        {requiresAddress && isValidSingleShippingDate && (
          <Alert type="blue-mid-light" className="mb-2" borderless>
            Your order will be directly delivered to the address chosen by{' '}
            <b>{vendorName}</b>.
            {vendorAttribution?.email_address && (
              <Fragment>
                <br />
                <br />
                Have any questions? Email them at{' '}
                <a
                  href={`mailto:${vendorAttribution.email_address}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {vendorAttribution.email_address}
                </a>
              </Fragment>
            )}
          </Alert>
        )}

        {requiredPrepaidNotice && (
          <Fragment>
            <h3 className="text-extra-bold text-faded">Reminder</h3>
            <Alert type="blue-mid-light" className="mb-2" borderless>
              This is a pre-order. After your proofs are delivered from this
              event, you will need to select your preferred image(s) for this
              order before it can be finalized and delivered.
            </Alert>
          </Fragment>
        )}

        {cart.length > 0 && (
          <Fragment>
            {pricing?.total_price.value > 0 && (
              <Fragment>
                <h3 className="text-extra-bold text-faded">Total</h3>
                <div className="rounded-box bg-gray-lightest">
                  <PriceBreakdownList
                    pricing={pricing}
                    loading={loadingPricing}
                  />
                </div>
              </Fragment>
            )}

            {cart.length > 0 && (
              <CheckoutCartPaymentPanel
                account={account}
                location={location}
                loading={loadingPricing}
                currency={pricing?.total_price.currency}
                finalPrice={pricing?.total_price.value}
                totalPrice={totalComputedPrice}
                onStripeToken={onCheckout}
                submitting={submitting}
                blockers={requirements.filter(
                  requirement => !requirement.condition,
                )}
                checkoutItems={checkoutItems}
                allowedPromoCodes={allowedPromoCodes}
                stripeModalProps={stripeModalProps}
              />
            )}
          </Fragment>
        )}
      </div>
    </Fragment>
  );
};

CheckoutCart.propTypes = propTypes;

const mapStateToProps = state => ({
  cart: getCheckoutCart(state),
  previousCart: getCheckoutPreviousCart(state),
  totalCartItems: getCheckoutCartTotalItems(state),
  checkoutItems: getCheckoutCartCheckoutItems(state),
  computedPrice: getCheckoutCartTotalPrice(state),
  requiresAddress: checkoutCartRequiresAddress(state),
  requiredPrepaidNotice: checkoutCartRequiresPrepaidNotice(state),
  authenticated: state.auth.authenticated,
  unconfiguredAlbumPrintPackageCartItems: getUnconfiguredAlbumPrintPackageCartItems(
    state,
  ),
  unconfiguredPurchasedPrintPackageCartItems: getUnconfiguredPurchasedPrintPackageCartItems(
    state,
  ),
  unconfiguredTaggedAlbumPrintPackageCartItems: getUnconfiguredTaggedAlbumPrintPackageCartItems(
    state,
  ),
});

const mapDispatchToProps = {
  onCartItemRemove: onCartItemRemoveAction,
  clearCart: clearCartAction,
  openAlert: openAlertAction,
};

const getAlbumMembershipGroup = graphql(getAlbumMembershipGroupQuery, {
  options: ({ location }) => {
    const match = matchPath(location.pathname, {
      path: '/app/shutterbug/hierarchy-items/:hierarchyItemId',
      exact: false,
    });
    const consumersMatch = matchPath(location.pathname, {
      path: '/consumer/matches/:albumMembershipGroupId',
      exact: false,
    });

    const albumMembershipGroupId =
      match?.params?.hierarchyItemId ??
      consumersMatch?.params?.albumMembershipGroupId;

    return {
      variables: {
        id: albumMembershipGroupId,
      },
      fetchPolicy: 'cache-first',
    };
  },
  skip: ({ location }) => {
    const match = matchPath(location.pathname, {
      path: '/app/shutterbug/hierarchy-items/:hierarchyItemId',
      exact: false,
    });
    const consumersMatch = matchPath(location.pathname, {
      path: '/consumer/matches/:albumMembershipGroupId',
      exact: false,
    });

    const albumMembershipGroupId =
      match?.params?.hierarchyItemId ??
      consumersMatch?.params?.albumMembershipGroupId;

    return !albumMembershipGroupId;
  },
});

const getAlbumFolderHierarchyShipping = graphql(
  AlbumFolderForHierarchyShippingSettings,
  {
    options: ({ hierarchyItemId, albumFolderId }) => {
      return {
        variables: {
          id: hierarchyItemId ?? albumFolderId,
        },
        fetchPolicy: 'cache-first',
      };
    },
    skip: ({ hierarchyItemId, albumFolderId }) =>
      !hierarchyItemId && !albumFolderId,
  },
);

const getCheckoutPrice = graphql(getCheckoutPriceQuery, {
  options: ({ account, checkoutItems, isValidSingleShippingDate }) => {
    const [address] = account.addresses;

    return {
      variables: {
        account_id: account.id,
        address_id: isValidSingleShippingDate ? undefined : address?.id,
        ...checkoutItems,
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
    };
  },
  skip: ({ account, cart }) => !account || !cart.length,
});

const withCheckoutMutation = graphql(checkoutMutation, {
  props: ({
    mutate,
    ownProps: { account, checkoutItems, isValidSingleShippingDate },
  }) => ({
    checkout: ({
      stripeTokenId,
      stripePaymentMethodId,
      email,
      promoCode,
      stripeConnectAccountId,
    }) =>
      mutate({
        variables: {
          input: {
            account_id: account.id,
            account_address_id: isValidSingleShippingDate
              ? undefined
              : account.addresses?.[0]?.id,
            stripe_token: stripeTokenId,
            email_address: email,
            promo_code: promoCode,
            platform: 'WEB',
            stripe_payment_method_id: stripePaymentMethodId,
            stripe_connect_account_id: stripeConnectAccountId,
            ...checkoutItems,
          },
        },
      }),
  }),
});

export { CheckoutCart as CheckoutCartComponent };

export default compose(
  withRouter,
  withApollo,
  connect(mapStateToProps, mapDispatchToProps),
  getAccount,
  mapProps(({ data, ...props }) => ({
    account: data?.me,
    ...props,
  })),
  getAlbumMembershipGroup,
  mapProps(({ data, ...props }) => ({
    albumMembershipGroup: data?.albumMembershipGroup,
    hierarchyItemId: data?.albumMembershipGroup?.is_album
      ? data?.albumMembershipGroup.album.root_album_folder?.id
      : data?.albumMembershipGroup?.id,
    ...props,
  })),
  getAlbumFolderHierarchyShipping,
  mapProps(({ data, ...props }) => {
    const { albumFolder } = data ?? {};
    const expiresAt = albumFolder?.hierarchy_shipping_settings?.expires_at
      ? new Date(albumFolder?.hierarchy_shipping_settings.expires_at)
      : null;

    return {
      isValidSingleShippingDate:
        !!albumFolder?.hierarchy_shipping_settings &&
        (!expiresAt || expiresAt > new Date()),
      vendor: albumFolder?.account,
      ...props,
    };
  }),
  getCheckoutPrice,
  mapProps(({ data, ...props }) => ({
    pricing: data?.checkoutPrice,
    loadingPricing: data?.loading,
    refetchPricing: data?.refetch,
    errorFetchingPricing: data?.error,
    itemsPriceBreakdown: [
      ...(data?.checkoutPrice?.items_price_breakdown?.photo_items ?? []),
    ],
    ...props,
  })),
  withCheckoutMutation,
)(CheckoutCart);
